// Подъем скелета Spine из формата 3.8 в формат 4.2.
//
// Официальный рантайм @esotericsoftware/spine-pixi-v8 читает только данные 4.x, а художники
// отдают экспорт из редактора Spine 3.8. Модуль правит разобранный JSON на лету, при загрузке.
// Данные 4.x проходят насквозь, поэтому переход на новый экспорт ничего не сломает.

/** Разобранный JSON скелета. Состав полей зависит от версии формата, поэтому он не типизирован. */
export type SkeletonJsonData = Record<string, any>;

type KeyFrame = Record<string, any>;

const SOURCE_VERSION = '3.8';
const TARGET_VERSION = '4.2.00';

/** Округляет число до шести знаков и убирает минус нуля. */
function round(value: number): number {
  const result = Math.round(value * 1e6) / 1e6;
  return result === 0 ? 0 : result;
}

/**
 * Разворачивает углы поворота в непрерывный ряд.
 *
 * Рантайм 3.8 брал между соседними ключами кратчайший путь: разницу он приводил к отрезку
 * от -180 до 180 и оттого проходил через ±180 без лишнего оборота. Рантайм 4.2 так не делает
 * и идёт от значения к значению напрямую. Поэтому пара ключей -165 и 163, записанная в 3.8
 * как поворот на 32 градуса назад, в 4.2 разворачивает кость на 328 градусов вперёд.
 *
 * Здесь та же кратчайшая разница складывается в непрерывный ряд: 4.2 читает его как есть,
 * и кость идёт тем же путём, каким шла в 3.8. Углы за пределами -180..180 рантайм понимает,
 * поэтому ряд можно не приводить обратно.
 */
export function unwrapAngles(keys: readonly KeyFrame[]): number[] {
  const angles: number[] = [];
  let previous = 0;

  for (const key of keys) {
    const angle: number = key.angle ?? 0;
    const last: number | undefined = angles[angles.length - 1];

    angles.push(last === undefined ? angle : last + shortest(angle - previous));
    previous = angle;
  }

  return angles;
}

/** Кратчайшая разница между углами, от -180 до 180. */
function shortest(delta: number): number {
  return delta - Math.floor(delta / 360 + 0.5) * 360;
}

/** Раскладывает цвет "rrggbbaa" на четыре компонента 0..1. */
function rgbaComponents(hex: string): number[] {
  return [0, 2, 4, 6].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255);
}

/** Раскладывает цвет "rrggbb" на три компонента 0..1. */
function rgbComponents(hex: string): number[] {
  return [0, 2, 4].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255);
}

/**
 * Переводит кривую Безье из 3.8 в 4.2.
 *
 * В 3.8 контрольные точки заданы в долях отрезка: curve = cx1, c2 = cy1, c3 = cx2, c4 = cy2.
 * В 4.2 та же кривая записана массивом абсолютных координат время/значение, по четыре числа
 * на каждый компонент таймлайна.
 */
function convertCurve(
  key: KeyFrame,
  next: KeyFrame,
  components: readonly (readonly [number, number])[],
): number[] | 'stepped' | undefined {
  if (!Object.hasOwn(key, 'curve'))
    return undefined;
  if (key.curve === 'stepped')
    return 'stepped';

  const cx1: number = key.curve;
  const cy1: number = key.c2 ?? 0;
  const cx2: number = key.c3 ?? 1;
  const cy2: number = key.c4 ?? 1;
  const time1: number = key.time ?? 0;
  const span: number = (next.time ?? 0) - time1;

  const curve: number[] = [];
  for (const [value1, value2] of components) {
    const delta = value2 - value1;
    curve.push(
      round(time1 + cx1 * span),
      round(value1 + cy1 * delta),
      round(time1 + cx2 * span),
      round(value1 + cy2 * delta),
    );
  }
  return curve;
}

/**
 * Пересобирает таймлайн покадрово.
 *
 * @param keys ключи в формате 3.8
 * @param toFrame строит ключ 4.2 без кривой
 * @param toValues отдает значения компонентов ключа, по ним считаются координаты кривой
 */
function convertTimeline(
  keys: readonly KeyFrame[],
  toFrame: (key: KeyFrame) => KeyFrame,
  toValues: (key: KeyFrame) => number[],
): KeyFrame[] {
  const values = keys.map(toValues);
  return keys.map((key, index) => {
    const frame = toFrame(key);
    const next = keys[index + 1];
    if (next === undefined)
      return frame;

    const components = values[index]!.map(
      (value, component) => [value, values[index + 1]![component]!] as const,
    );
    const curve = convertCurve(key, next, components);
    if (curve !== undefined)
      frame.curve = curve;
    return frame;
  });
}

/** Создает ключ 4.2 и переносит время, если оно задано. */
function frameOf(key: KeyFrame, fields: Record<string, unknown>): KeyFrame {
  const frame: KeyFrame = {};
  if (key.time !== undefined)
    frame.time = key.time;
  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined)
      frame[name] = value;
  }
  return frame;
}

function convertSlotTimelines(slotName: string, slotMap: Record<string, KeyFrame[]>): KeyFrame {
  const result: KeyFrame = {};
  for (const [name, keys] of Object.entries(slotMap)) {
    switch (name) {
      case 'attachment':
        result.attachment = keys;
        break;
      case 'color':
        result.rgba = convertTimeline(
          keys,
          (key) => frameOf(key, { color: key.color }),
          (key) => rgbaComponents(key.color),
        );
        break;
      case 'twoColor':
        result.rgba2 = convertTimeline(
          keys,
          (key) => frameOf(key, { light: key.light, dark: key.dark }),
          (key) => [...rgbaComponents(key.light), ...rgbComponents(key.dark)],
        );
        break;
      default:
        throw new Error(`Слот ${slotName}: неизвестный таймлайн 3.8 "${name}"`);
    }
  }
  return result;
}

function convertBoneTimelines(boneName: string, boneMap: Record<string, KeyFrame[]>): KeyFrame {
  const result: KeyFrame = {};
  for (const [name, keys] of Object.entries(boneMap)) {
    switch (name) {
      case 'rotate': {
        // Углы разворачиваются до перевода: по ним же считаются координаты кривой Безье,
        // а она живёт в том же пространстве значений, что и сами ключи.
        const angles: number[] = unwrapAngles(keys);
        const straight: KeyFrame[] = keys.map((key, index) => ({ ...key, angle: round(angles[index]!) }));

        result.rotate = convertTimeline(
          straight,
          (key) => frameOf(key, { value: key.angle }),
          (key) => [key.angle ?? 0],
        );
        break;
      }
      case 'translate':
      case 'shear':
        result[name] = convertTimeline(
          keys,
          (key) => frameOf(key, { x: key.x, y: key.y }),
          (key) => [key.x ?? 0, key.y ?? 0],
        );
        break;
      case 'scale':
        result.scale = convertTimeline(
          keys,
          (key) => frameOf(key, { x: key.x, y: key.y }),
          (key) => [key.x ?? 1, key.y ?? 1],
        );
        break;
      default:
        throw new Error(`Кость ${boneName}: неизвестный таймлайн 3.8 "${name}"`);
    }
  }
  return result;
}

function convertIkTimeline(keys: readonly KeyFrame[]): KeyFrame[] {
  return convertTimeline(
    keys,
    (key) =>
      frameOf(key, {
        mix: key.mix,
        softness: key.softness,
        bendPositive: key.bendPositive,
        compress: key.compress,
        stretch: key.stretch,
      }),
    (key) => [key.mix ?? 1, key.softness ?? 0],
  );
}

function convertTransformTimeline(keys: readonly KeyFrame[]): KeyFrame[] {
  return convertTimeline(
    keys,
    (key) =>
      frameOf(key, {
        mixRotate: key.rotateMix,
        mixX: key.translateMix,
        mixY: key.translateMix,
        mixScaleX: key.scaleMix,
        mixScaleY: key.scaleMix,
        mixShearY: key.shearMix,
      }),
    (key) => {
      const translate: number = key.translateMix ?? 1;
      const scale: number = key.scaleMix ?? 1;
      return [key.rotateMix ?? 1, translate, translate, scale, scale, key.shearMix ?? 1];
    },
  );
}

function convertPathTimelines(
  constraintName: string,
  constraintMap: Record<string, KeyFrame[]>,
): KeyFrame {
  const result: KeyFrame = {};
  for (const [name, keys] of Object.entries(constraintMap)) {
    switch (name) {
      case 'position':
      case 'spacing':
        result[name] = convertTimeline(
          keys,
          (key) => frameOf(key, { value: key[name] }),
          (key) => [key[name] ?? 0],
        );
        break;
      case 'mix':
        result.mix = convertTimeline(
          keys,
          (key) =>
            frameOf(key, {
              mixRotate: key.rotateMix,
              mixX: key.translateMix,
              mixY: key.translateMix,
            }),
          (key) => {
            const translate: number = key.translateMix ?? 1;
            return [key.rotateMix ?? 1, translate, translate];
          },
        );
        break;
      default:
        throw new Error(`Ограничитель пути ${constraintName}: неизвестный таймлайн 3.8 "${name}"`);
    }
  }
  return result;
}

/** Переносит деформацию мешей: 3.8 хранит ее в "deform", 4.2 — в "attachments". */
function convertDeform(deformMap: Record<string, any>): KeyFrame {
  const attachments: KeyFrame = {};
  for (const [skinName, slots] of Object.entries(deformMap)) {
    const skin: KeyFrame = {};
    for (const [slotName, slotAttachments] of Object.entries(slots as Record<string, any>)) {
      const slot: KeyFrame = {};
      for (const [attachmentName, keys] of Object.entries(slotAttachments as Record<string, any>)) {
        // Рантайм 4.2 читает кривую деформации на отрезке значений 0..1.
        slot[attachmentName] = {
          deform: convertTimeline(
            keys as KeyFrame[],
            (key) => frameOf(key, { offset: key.offset, vertices: key.vertices }),
            () => [0, 1],
          ),
        };
      }
      skin[slotName] = slot;
    }
    attachments[skinName] = skin;
  }
  return attachments;
}

function convertAnimation(animationName: string, animation: Record<string, any>): KeyFrame {
  const result: KeyFrame = {};

  for (const [name, value] of Object.entries(animation)) {
    switch (name) {
      case 'slots': {
        const slots: KeyFrame = {};
        for (const [slotName, slotMap] of Object.entries(value as Record<string, any>)) {
          slots[slotName] = convertSlotTimelines(slotName, slotMap);
        }
        result.slots = slots;
        break;
      }
      case 'bones': {
        const bones: KeyFrame = {};
        for (const [boneName, boneMap] of Object.entries(value as Record<string, any>)) {
          bones[boneName] = convertBoneTimelines(boneName, boneMap);
        }
        result.bones = bones;
        break;
      }
      case 'ik': {
        const constraints: KeyFrame = {};
        for (const [constraintName, keys] of Object.entries(value as Record<string, any>)) {
          constraints[constraintName] = convertIkTimeline(keys);
        }
        result.ik = constraints;
        break;
      }
      case 'transform': {
        const constraints: KeyFrame = {};
        for (const [constraintName, keys] of Object.entries(value as Record<string, any>)) {
          constraints[constraintName] = convertTransformTimeline(keys);
        }
        result.transform = constraints;
        break;
      }
      case 'path':
      case 'paths': {
        const constraints: KeyFrame = {};
        for (const [constraintName, constraintMap] of Object.entries(value as Record<string, any>)) {
          constraints[constraintName] = convertPathTimelines(constraintName, constraintMap);
        }
        result.path = constraints;
        break;
      }
      case 'deform':
        result.attachments = convertDeform(value);
        break;
      case 'drawOrder':
      case 'draworder':
        result.drawOrder = value;
        break;
      case 'events':
        result.events = value;
        break;
      default:
        throw new Error(`Анимация ${animationName}: неизвестная секция 3.8 "${name}"`);
    }
  }

  return result;
}

/** Переименовывает поля вложений: в 4.2 связанный меш управляет таймлайнами через "timelines". */
function convertSkins(skins: readonly Record<string, any>[]): Record<string, any>[] {
  return skins.map((skin) => {
    const attachments: KeyFrame = {};
    for (const [slotName, slotAttachments] of Object.entries(
      (skin.attachments ?? {}) as Record<string, any>,
    )) {
      const slot: KeyFrame = {};
      for (const [attachmentName, attachment] of Object.entries(
        slotAttachments as Record<string, any>,
      )) {
        if (attachment.type === 'linkedmesh' && Object.hasOwn(attachment, 'deform')) {
          const { deform, ...rest } = attachment;
          slot[attachmentName] = { ...rest, timelines: deform };
        } else {
          slot[attachmentName] = attachment;
        }
      }
      attachments[slotName] = slot;
    }
    return { ...skin, attachments };
  });
}

function convertTransformConstraints(
  constraints: readonly Record<string, any>[],
): Record<string, any>[] {
  return constraints.map((constraint) => {
    const { rotateMix, translateMix, scaleMix, shearMix, ...rest } = constraint;
    const result: KeyFrame = { ...rest };
    if (rotateMix !== undefined)
      result.mixRotate = rotateMix;
    if (translateMix !== undefined) {
      result.mixX = translateMix;
      result.mixY = translateMix;
    }
    if (scaleMix !== undefined) {
      result.mixScaleX = scaleMix;
      result.mixScaleY = scaleMix;
    }
    if (shearMix !== undefined)
      result.mixShearY = shearMix;
    return result;
  });
}

function convertPathConstraints(
  constraints: readonly Record<string, any>[],
): Record<string, any>[] {
  return constraints.map((constraint) => {
    const { rotateMix, translateMix, ...rest } = constraint;
    const result: KeyFrame = { ...rest };
    if (rotateMix !== undefined)
      result.mixRotate = rotateMix;
    if (translateMix !== undefined) {
      result.mixX = translateMix;
      result.mixY = translateMix;
    }
    return result;
  });
}

/** Поле наследования трансформации кости в 4.2 называется "inherit". */
function convertBones(bones: readonly Record<string, any>[]): Record<string, any>[] {
  return bones.map((bone) => {
    if (!Object.hasOwn(bone, 'transform'))
      return bone;
    const { transform, ...rest } = bone;
    return { ...rest, inherit: transform };
  });
}

/**
 * Поднимает скелет 3.8 до 4.2. Скелет 4.x возвращается как есть.
 *
 * @throws если версия формата не 3.8 и не 4.x
 */
export function upgradeSkeleton(skeleton: SkeletonJsonData): SkeletonJsonData {
  const version: string = skeleton.skeleton?.spine ?? '';
  if (version.startsWith('4.'))
    return skeleton;
  if (!version.startsWith(SOURCE_VERSION)) {
    throw new Error(`Ожидается экспорт Spine ${SOURCE_VERSION} или 4.x, получена версия "${version}"`);
  }

  const result: SkeletonJsonData = { skeleton: { ...skeleton.skeleton, spine: TARGET_VERSION } };

  if (skeleton.bones)
    result.bones = convertBones(skeleton.bones);
  if (skeleton.slots)
    result.slots = skeleton.slots;
  if (skeleton.ik)
    result.ik = skeleton.ik;
  if (skeleton.transform)
    result.transform = convertTransformConstraints(skeleton.transform);
  if (skeleton.path)
    result.path = convertPathConstraints(skeleton.path);
  if (skeleton.skins)
    result.skins = convertSkins(skeleton.skins);
  if (skeleton.events)
    result.events = skeleton.events;

  if (skeleton.animations) {
    const animations: KeyFrame = {};
    for (const [name, animation] of Object.entries(skeleton.animations as Record<string, any>)) {
      animations[name] = convertAnimation(name, animation);
    }
    result.animations = animations;
  }

  return result;
}
